/**
 * Dunea Domains (DEV-121) — search, buy, and manage domains without leaving Dunea.
 * Purchase flow: search → pick an available domain → choose a live deployment to
 * connect it to (REQUIRED) → Stripe Checkout → (webhook) register + DNS + connect
 * + SSL. Price is computed server-side; the client only sends domain + deployment.
 * After checkout we poll the domain until it goes active or failed.
 *
 * Presentation only — no backend logic here. Uses the existing domains APIs.
 * Chrome matches Projects.tsx: sticky translucent header, 1280px column, flat
 * editorial cards, one lime accent per view.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import {
  apiSearchDomains,
  apiListDomains,
  apiListDeployments,
  apiPurchaseDomain,
  apiResumeDomainCheckout,
  apiRemoveDomain,
  apiGetDomain,
  apiQuoteTransferIn,
  apiStartTransferIn,
  apiGetRegistrant,
  type DomainSearchResult,
  type ManagedDomain,
} from '~/lib/api/domains';
import { RegistrantForm } from '~/components/domains/RegistrantForm';
import { CARD, StatusPill, TERMINAL, IN_PROGRESS, fmtMoney, fmtDate } from '~/components/domains/domainUi';
import { usePlanAccess, handlePlanRequired, showPlans, blockAndShowPlans } from '~/lib/planGate';

/** The headline search result — the exact domain searched. Flat, no glow. */
function DomainResultCard({ r, selected, onBuy }: { r: DomainSearchResult; selected: boolean; onBuy: () => void }) {
  if (!r.available) {
    return (
      <div className={classNames(CARD, 'flex items-center gap-3 p-4 opacity-60')}>
        <div className="i-ph:lock-simple shrink-0 text-[16px] text-faint" />
        <div className="ui-mono min-w-0 flex-1 truncate text-[14px] text-secondary">{r.domain}</div>
        <span className="ui-pill text-muted">Taken</span>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        CARD,
        'flex flex-col p-5 transition-colors duration-200',
        selected ? 'border-accent/50' : 'hover:border-subtle',
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="i-ph:globe shrink-0 text-[16px] text-accent" />
          <div className="ui-mono min-w-0 truncate text-[15px] text-primary">{r.domain}</div>
        </div>
        {r.premium && <span className="ui-pill text-warning border-warning/30">Premium</span>}
      </div>

      <div className="mb-4 flex items-end justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-success">
          <span className="ui-dot ui-dot-ok" />
          Available
        </span>
        <div className="text-right">
          <div className="text-[19px] font-semibold leading-none text-primary [font-variant-numeric:tabular-nums]">
            {fmtMoney(r.price?.registerPrice ?? null, r.price?.currency)}
            <span className="ml-1 text-[11px] font-normal text-muted">/yr</span>
          </div>
          {r.price?.renewPrice != null && (
            <div className="mt-1 text-[10.5px] text-muted">
              Renews {fmtMoney(r.price.renewPrice, r.price.currency)}/yr
            </div>
          )}
        </div>
      </div>

      <button className="ui-btn-accent w-full justify-center" onClick={onBuy}>
        {selected ? 'Selected' : 'Buy domain'}
        <div className="i-ph:arrow-right text-[13px]" />
      </button>
    </div>
  );
}

/** A compact alternative-domain row inside the "More options" list. */
function DomainResultRow({ r, selected, onBuy }: { r: DomainSearchResult; selected: boolean; onBuy: () => void }) {
  return (
    <div
      className={classNames(
        'flex items-center gap-3 border-t border-hairline px-4 py-3 first:border-t-0 transition-colors',
        !r.available && 'opacity-60',
        selected ? 'bg-accent/[0.04]' : 'hover:bg-white/[0.02]',
      )}
    >
      <div
        className={classNames(
          'shrink-0 text-[15px]',
          r.available ? 'i-ph:globe text-accent' : 'i-ph:lock-simple text-faint',
        )}
      />
      <span className="ui-mono min-w-0 flex-1 truncate text-[13.5px] text-primary">{r.domain}</span>

      {r.premium && <span className="ui-pill hidden text-warning border-warning/30 sm:inline-flex">Premium</span>}

      {r.available ? (
        <>
          <div className="shrink-0 text-[13.5px] font-semibold text-primary [font-variant-numeric:tabular-nums]">
            {fmtMoney(r.price?.registerPrice ?? null, r.price?.currency)}
            <span className="ml-0.5 text-[10px] font-normal text-muted">/yr</span>
          </div>
          <button className="ui-btn-secondary shrink-0 px-3 py-1.5 text-[12.5px]" onClick={onBuy}>
            {selected ? 'Selected' : 'Buy'}
          </button>
        </>
      ) : (
        <span className="ui-pill shrink-0 text-muted">Taken</span>
      )}
    </div>
  );
}

/** Restrained loading state while a search runs — flat placeholder rows. */
function SearchSkeleton({ query }: { query: string }) {
  return (
    <section className="mb-12">
      <div className="mb-4 flex items-center gap-2 text-[12.5px] text-secondary">
        <div className="i-svg-spinners:90-ring-with-bg text-[14px] text-accent" />
        Searching <span className="ui-mono text-muted">{query.trim()}</span>…
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={classNames(CARD, 'p-5')}>
            <div className="mb-4 flex items-center gap-2.5">
              <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-white/[0.05]" />
              <div className="h-3.5 w-2/3 animate-pulse rounded bg-white/[0.05]" />
            </div>
            <div className="mb-4 flex items-center justify-between">
              <div className="h-3.5 w-20 animate-pulse rounded bg-white/[0.04]" />
              <div className="h-4 w-16 animate-pulse rounded bg-white/[0.04]" />
            </div>
            <div className="h-9 w-full animate-pulse rounded-full bg-white/[0.04]" />
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * A clickable domain card on the dashboard. The whole card opens the detail
 * page; inner links/buttons stop propagation. A div+role="button" (not a
 * <button>) so nested interactive elements stay valid HTML. Pending (unpaid)
 * orders swap the info row/footer for price + Complete payment / Remove.
 */
function DomainCard({
  d,
  onOpen,
  onResume,
  onRemove,
  busy,
}: {
  d: ManagedDomain;
  onOpen: () => void;
  onResume?: () => void;
  onRemove?: () => void;
  busy?: boolean;
}) {
  const isPending = d.status === 'pending';
  const visitHref = d.deployUrl ? (d.source === 'connected' ? `https://${d.domain}` : d.deployUrl) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={classNames(
        CARD,
        'group cursor-pointer p-5 text-left outline-none transition-all duration-200 can-hover:hover:-translate-y-0.5',
        isPending ? 'hover:border-warning/40' : 'hover:border-subtle focus-visible:border-accent/50',
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="i-ph:globe shrink-0 text-[15px] text-accent" />
          <div className="min-w-0">
            <div className="ui-mono truncate text-[14px] text-primary">{d.domain}</div>
            <div className="text-[11px] text-muted">
              {d.source === 'purchased' ? 'Purchased with Dunea' : 'Connected domain'}
            </div>
          </div>
        </div>
        <StatusPill status={d.status} />
      </div>

      {isPending ? (
        <div className="flex items-center justify-between gap-2 border-t border-hairline pt-3">
          <div>
            <div className="ui-caption mb-1">Total</div>
            <div className="text-[14px] font-medium text-primary">
              {fmtMoney(d.pricePaid, d.currency ?? 'usd')} <span className="text-[11px] text-muted">/ yr</span>
            </div>
          </div>
          <div className="text-right text-[11px] text-muted">
            Not charged yet —<br />
            complete or remove.
          </div>
        </div>
      ) : (
        <div className="border-t border-hairline pt-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <div className="ui-caption mb-1">SSL</div>
              <span
                className={classNames(
                  'inline-flex items-center gap-1 text-[12px]',
                  d.sslStatus === 'active' || d.source === 'connected' ? 'text-success' : 'text-muted',
                )}
              >
                <div className="i-ph:lock-key text-[12px]" />
                {d.sslStatus === 'active' ? 'Secured' : d.source === 'connected' ? 'Auto' : (d.sslStatus ?? 'Pending')}
              </span>
            </div>
            <div>
              <div className="ui-caption mb-1">Project</div>
              <div className="ui-mono truncate text-[12px] text-secondary">
                {d.connectedSubdomain ? `${d.connectedSubdomain}` : '—'}
              </div>
            </div>
            {d.source === 'purchased' && (
              <>
                <div>
                  <div className="ui-caption mb-1">Registered</div>
                  <div className="text-[12px] text-secondary">{fmtDate(d.registeredAt)}</div>
                </div>
                <div>
                  <div className="ui-caption mb-1">Renews</div>
                  <div className="text-[12px] text-secondary">{fmtDate(d.expiresAt)}</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isPending ? (
        <div className="mt-3 flex items-center gap-2 border-t border-hairline pt-3">
          <button
            className="ui-btn-accent flex-1 justify-center"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onResume?.();
            }}
          >
            <div className="i-ph:credit-card text-[13px]" />
            Complete payment
          </button>
          <button
            className="ui-btn-ghost text-danger hover:bg-danger/10"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
          >
            <div className="i-ph:trash text-[13px]" />
            Remove
          </button>
        </div>
      ) : (
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-hairline pt-3">
          {visitHref ? (
            <a
              href={visitHref}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-[12px] text-secondary transition-colors hover:text-accent"
            >
              <div className="i-ph:arrow-square-out text-[13px]" />
              Visit
            </a>
          ) : (
            <span />
          )}
          <span className="inline-flex items-center gap-1 text-[11.5px] text-muted transition-colors group-hover:text-primary">
            Manage
            <div className="i-ph:arrow-right text-[12px]" />
          </span>
        </div>
      )}
    </div>
  );
}

export default function DomainsPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DomainSearchResult[] | null>(null);
  // The exact domain the user searched for (the headline result); alternatives
  // are everything else. Derived from the query the backend echoes back.
  const [searchPrimary, setSearchPrimary] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [deploymentId, setDeploymentId] = useState('');
  const [registrantModal, setRegistrantModal] = useState<null | 'gate' | 'edit'>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Buying, transferring and connecting are paid-plan features (the server refuses
  // them on free). Managing domains already owned stays open on every plan, so the
  // page itself isn't gated — only the actions that spend money or attach a domain.
  const { isFree } = usePlanAccess();

  /** Gate a paid-only action: opens the plans dialog and returns false on free. */
  function requirePaidPlan(): boolean {
    if (isFree) {
      blockAndShowPlans('custom_domain');
      return false;
    }

    return true;
  }

  // The buyer's registrant info — domains are registered in their name.
  const { data: registrant, refetch: refetchRegistrant } = useQuery({
    queryKey: ['registrant'],
    queryFn: apiGetRegistrant,
  });

  const { data: deployments = [] } = useQuery({ queryKey: ['deployments'], queryFn: apiListDeployments });
  const liveDeployments = useMemo(() => deployments.filter((d) => d.status === 'live'), [deployments]);

  const { data: managed = [], isLoading: managedLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: apiListDomains,
    // Keep the dashboard live while any PAID purchase is still being fulfilled. An
    // idle `pending` (unpaid) order won't change on its own, so it doesn't poll,
    // and inbound transfers (days at the registries) poll gently.
    refetchInterval: (q) => {
      const rows = (q.state.data ?? []).filter((d) => d.source === 'purchased' && d.status !== 'pending');

      if (rows.some((d) => !TERMINAL.has(d.status) && d.status !== 'transferring')) {
        return 4000;
      }

      return rows.some((d) => d.status === 'transferring') ? 30000 : false;
    },
  });

  // Post-checkout: poll the just-purchased domain until active or failed.
  const purchasedId = params.get('domains') === 'purchased' ? params.get('domainId') : null;
  const { data: polled } = useQuery({
    queryKey: ['domain', purchasedId],
    queryFn: () => apiGetDomain(purchasedId as string),
    enabled: Boolean(purchasedId),
    refetchInterval: (q) =>
      q.state.data && TERMINAL.has(q.state.data.status)
        ? false
        : q.state.data?.status === 'transferring'
          ? 30000
          : 3000,
  });

  useEffect(() => {
    if (params.get('domains') === 'cancel') {
      toast('Checkout canceled.');
      setParams({}, { replace: true });
    }
  }, []);

  const searchMutation = useMutation({
    mutationFn: (q: string) => apiSearchDomains(q),
    onSuccess: (data) => {
      setResults(data.results);

      // The headline result: the exact domain searched, or `.com` for a bare label.
      const q = data.query.trim().toLowerCase();
      setSearchPrimary(q.includes('.') ? q : `${q}.com`);
    },
    onError: () => toast.error('Domain search failed. Please try again.'),
  });

  const purchaseMutation = useMutation({
    mutationFn: ({ domain, dep }: { domain: string; dep: string }) => apiPurchaseDomain(domain, dep),
    onSuccess: ({ url }) => {
      window.location.href = url; // Stripe Checkout — same as subscription/top-up flows.
    },
    onError: (err: unknown) => {
      // Server-side gate: plan lapsed since the page loaded → open the plans dialog.
      if (handlePlanRequired(err)) {
        return;
      }

      const data = (err as { response?: { data?: { code?: string; message?: string } } })?.response?.data;

      // Server-side gate: user hasn't provided registrant info yet → pop the form.
      if (data?.code === 'REGISTRANT_REQUIRED') {
        setRegistrantModal('gate');
        return;
      }

      toast.error(data?.message || 'Could not start your purchase. Please try again.');
    },
  });

  // Resume payment for a pending order → back to Stripe Checkout.
  const resumeMutation = useMutation({
    mutationFn: (id: string) => apiResumeDomainCheckout(id),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: unknown) => {
      if (handlePlanRequired(err)) {
        return;
      }

      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not resume your checkout. Please try again.');
    },
  });

  // Remove a pending/failed order the user doesn't want.
  const removeMutation = useMutation({
    mutationFn: (id: string) => apiRemoveDomain(id),
    onSuccess: () => {
      toast.success('Order removed.');
      queryClient.invalidateQueries({ queryKey: ['domains'] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not remove this order.');
    },
  });

  // ─── Transfer in (bring a domain from another registrar) ───
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferDomain, setTransferDomain] = useState('');
  const [transferAuthCode, setTransferAuthCode] = useState('');
  const [transferQuote, setTransferQuote] = useState<{ domain: string; transferPriceUsd: number } | null>(null);

  const transferQuoteMutation = useMutation({
    mutationFn: (domain: string) => apiQuoteTransferIn(domain),
    onSuccess: (quote) => setTransferQuote(quote),
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not price that transfer.');
    },
  });

  const transferStartMutation = useMutation({
    mutationFn: ({ domain, authCode }: { domain: string; authCode: string }) => apiStartTransferIn(domain, authCode),
    onSuccess: ({ url }) => {
      window.location.href = url; // hosted checkout, same as purchases
    },
    onError: (err: unknown) => {
      if (handlePlanRequired(err)) {
        return;
      }

      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not start the transfer.');
    },
  });

  // Buy → register in the user's name: require registrant info first, then checkout.
  function startPurchase() {
    if (!selected || !deploymentId) {
      return;
    }

    // Plan gate before the registrant gate — no point collecting WHOIS details for
    // a purchase the server will refuse.
    if (!requirePaidPlan()) {
      return;
    }

    if (!registrant?.hasContact) {
      setRegistrantModal('gate'); // collect registrant info before charging
      return;
    }

    purchaseMutation.mutate({ domain: selected, dep: deploymentId });
  }

  const stats = useMemo(() => {
    // "Purchased" counts actually-bought domains — never unpaid `pending` reservations.
    const purchased = managed.filter((d) => d.source === 'purchased' && d.status !== 'pending').length;
    const active = managed.filter((d) => d.status === 'active' || d.status === 'connected').length;
    const projects = new Set(managed.map((d) => d.deploymentId).filter(Boolean)).size;
    const ssl = managed.filter((d) => d.sslStatus === 'active' || d.source === 'connected').length;

    return { total: managed.length, purchased, active, projects, ssl };
  }, [managed]);

  // Three buckets, GoDaddy-style:
  //  • pending    — unpaid orders (Complete payment / Remove)
  //  • inProgress — paid & being fulfilled (registering/connecting/SSL)
  //  • settled    — active / connected / failed
  const groups = useMemo(() => {
    const pending = managed.filter((d) => d.source === 'purchased' && d.status === 'pending');
    const inProgress = managed.filter(
      (d) => d.source === 'purchased' && IN_PROGRESS.has(d.status) && d.status !== 'pending' && d.status !== 'active',
    );
    const placed = new Set([...pending, ...inProgress]);
    const settled = managed.filter((d) => !placed.has(d));

    return { pending, inProgress, settled };
  }, [managed]);

  function onSearch(e: React.FormEvent) {
    e.preventDefault();

    const q = query.trim();

    if (q) {
      searchMutation.mutate(q);
    }
  }

  const availableCount = results?.filter((r) => r.available).length ?? 0;
  const selectedResult = selected ? (results?.find((r) => r.domain === selected) ?? null) : null;
  const hasDomains = managed.length > 0;

  return (
    <div className="ui-root min-h-dvh bg-page">
      {/* Sticky translucent header — matches Projects.tsx chrome. */}
      <header className="sticky top-0 z-30 border-b border-hairline bg-page/70 backdrop-blur-[14px]">
        <div className="mx-auto flex max-w-[1280px] items-center gap-2.5 px-4 py-3 sm:gap-3 sm:px-7">
          <Link
            to="/"
            title="Back to Dunea"
            className="ui-btn-icon text-secondary hover:text-primary"
            aria-label="Back to Dunea"
          >
            <div className="i-ph:arrow-left text-[16px]" />
          </Link>
          <div className="flex items-center gap-2">
            <img src="/dunea-mark.svg" width={20} height={20} alt="" />
            <span className="ui-brand hidden text-[17px] text-primary sm:inline">Dunea</span>
          </div>
          <span className="text-faint">/</span>
          <span className="text-[13px] text-secondary">Domains</span>
          <div className="flex-1" />
          {isFree && (
            <button onClick={() => showPlans('domains_page')} className="ui-btn-secondary text-[12.5px]">
              View plans
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] px-4 pb-20 pt-7 sm:px-7 sm:pt-10">
        {/* ─── Editorial headline + search ─── */}
        <div className="mb-8">
          <span className="ui-caption">Domains</span>
          <h1 className="mt-2 font-instrument text-[32px] leading-[1.05] tracking-[-0.02em] text-primary sm:text-[44px]">
            {hasDomains ? (
              <>
                {stats.total} {stats.total === 1 ? 'domain' : 'domains'},{' '}
                <i className="text-accent">{stats.active} live.</i>
              </>
            ) : (
              <>
                Give your site a <i className="text-accent">real address.</i>
              </>
            )}
          </h1>
          <p className="mt-3 max-w-lg text-[13.5px] leading-relaxed text-secondary">
            Search and register a domain, or transfer one in — Dunea sets up DNS, SSL, and connects it to your project
            automatically.
          </p>

          {/* Search */}
          <form onSubmit={onSearch} className="mt-6 flex max-w-xl items-center gap-2.5">
            <div className="relative flex-1">
              <div className="i-ph:magnifying-glass pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[16px] text-muted" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for yourbrand.com…"
                aria-label="Search for a domain"
                className="ui-input h-11 w-full pl-10 text-[14px]"
              />
            </div>
            <button
              type="submit"
              disabled={searchMutation.isPending || !query.trim()}
              className="ui-btn-accent h-11 px-5 text-[13px]"
            >
              {searchMutation.isPending ? (
                <div className="i-svg-spinners:90-ring-with-bg text-base" />
              ) : (
                <>
                  Search
                  <div className="i-ph:arrow-right text-[13px]" />
                </>
              )}
            </button>
          </form>

          {/* Transfer-in disclosure */}
          <button
            onClick={() => setTransferOpen((v) => !v)}
            className="mt-4 inline-flex flex-wrap items-center gap-1.5 text-left text-[12.5px] text-muted transition-colors hover:text-secondary"
          >
            <div className="i-ph:swap text-[14px]" />
            Already own a domain elsewhere? Transfer it in
            <div
              className={classNames('i-ph:caret-down text-[12px] transition-transform', transferOpen && 'rotate-180')}
            />
          </button>

          {transferOpen && (
            <div className={classNames(CARD, 'mt-4 max-w-xl p-4 sm:p-5')}>
              <h4 className="mb-1.5 text-[13px] font-medium text-primary">Transfer a domain to Dunea</h4>
              <p className="mb-4 text-[12px] leading-relaxed text-muted">
                Unlock the domain at your current registrar and grab its transfer (EPP) code first. Transfers include a
                1-year extension and usually take a few days; your site keeps working throughout. Domains registered or
                transferred in the last 60 days can&apos;t be moved yet.
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={transferDomain}
                  onChange={(e) => {
                    setTransferDomain(e.target.value);
                    setTransferQuote(null);
                  }}
                  placeholder="yourbrand.com"
                  aria-label="Domain to transfer"
                  className="ui-input ui-input-mono min-w-48 flex-1 text-[13px]"
                />
                {!transferQuote && (
                  <button
                    className="ui-btn-secondary shrink-0"
                    disabled={transferQuoteMutation.isPending || !transferDomain.trim()}
                    onClick={() => transferQuoteMutation.mutate(transferDomain.trim().toLowerCase())}
                  >
                    {transferQuoteMutation.isPending ? (
                      <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                    ) : (
                      <div className="i-ph:calculator text-[14px]" />
                    )}
                    Get price
                  </button>
                )}
              </div>

              {transferQuote && (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-hairline bg-sunken px-3.5 py-2.5">
                    <span className="ui-mono min-w-0 break-all text-[13px] text-primary">{transferQuote.domain}</span>
                    <span className="text-[14px] font-semibold text-primary">
                      ${transferQuote.transferPriceUsd.toFixed(2)}
                      <span className="ml-1 text-[11px] font-normal text-muted">incl. 1-year extension</span>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      value={transferAuthCode}
                      onChange={(e) => setTransferAuthCode(e.target.value)}
                      placeholder="Transfer (EPP) code from your registrar"
                      aria-label="Transfer auth code"
                      className="ui-input ui-input-mono min-w-48 flex-1 text-[13px]"
                    />
                    <button
                      className="ui-btn-accent shrink-0"
                      disabled={transferStartMutation.isPending || !transferAuthCode.trim()}
                      onClick={() => {
                        if (!requirePaidPlan()) {
                          return;
                        }

                        transferStartMutation.mutate({
                          domain: transferQuote.domain,
                          authCode: transferAuthCode.trim(),
                        });
                      }}
                    >
                      {transferStartMutation.isPending ? (
                        <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                      ) : (
                        <div className="i-ph:credit-card text-[14px]" />
                      )}
                      Pay &amp; start transfer
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─── Post-checkout status ─── */}
        {purchasedId && polled && (
          <div className={classNames(CARD, 'mb-10 flex items-center gap-3 p-4 sm:gap-4 sm:p-5')}>
            <div
              className={classNames(
                'shrink-0 text-2xl',
                polled.status === 'active'
                  ? 'i-ph:check-circle text-success'
                  : polled.status === 'failed'
                    ? 'i-ph:warning-circle text-danger'
                    : 'i-svg-spinners:90-ring-with-bg text-accent',
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="ui-mono min-w-0 break-all text-[14px] text-primary">{polled.domain}</span>
                <StatusPill status={polled.status} />
              </div>
              <div className="mt-1 text-[12.5px] leading-relaxed text-muted">
                {polled.status === 'active'
                  ? 'Registered and connected — SSL is provisioning and your site will be live shortly.'
                  : polled.status === 'failed'
                    ? `Registration failed: ${polled.errorMessage ?? 'unknown error'}. Our team can recover this — your payment is safe.`
                    : 'Payment received — registering your domain and configuring DNS. This can take a minute.'}
              </div>
            </div>
          </div>
        )}

        {/* ─── Search loading ─── */}
        {searchMutation.isPending && <SearchSkeleton query={query} />}

        {/* ─── Search results ─── */}
        {results && !searchMutation.isPending && (
          <section className="mb-12">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="ui-caption">
                Results for <span className="ui-mono text-secondary">{results.length ? query.trim() : ''}</span>
              </h2>
              {results.length > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-success">
                  <span className="ui-dot ui-dot-ok" />
                  {availableCount} available
                </span>
              )}
            </div>

            {results.length === 0 ? (
              <div className={classNames(CARD, 'px-5 py-10 text-center')}>
                <div className="i-ph:magnifying-glass mx-auto mb-2 text-2xl text-faint" />
                <div className="text-[13px] text-muted">No results for that search — try a different name.</div>
              </div>
            ) : (
              (() => {
                const onBuy = (domain: string) => {
                  setSelected(domain);
                  setDeploymentId(liveDeployments[0]?.id ?? '');
                };

                // The searched name is the headline; everything else is an
                // alternative. Fall back to the first available result (or the
                // first result) when the exact match isn't in the set.
                const primary = searchPrimary
                  ? (results.find((r) => r.domain === searchPrimary) ?? results.find((r) => r.available) ?? results[0])
                  : (results.find((r) => r.available) ?? results[0]);
                const alternatives = results
                  .filter((r) => r !== primary)
                  .sort((a, b) => Number(b.available) - Number(a.available));

                return (
                  <div className="space-y-4">
                    {/* One card — the domain they searched for */}
                    <DomainResultCard
                      r={primary}
                      selected={selected === primary.domain}
                      onBuy={() => onBuy(primary.domain)}
                    />

                    {/* …and a list — alternatives across other extensions */}
                    {alternatives.length > 0 && (
                      <div>
                        <div className="ui-caption mb-2 px-1">More options</div>
                        <div className={classNames(CARD, 'overflow-hidden')}>
                          {alternatives.map((r) => (
                            <DomainResultRow
                              key={r.domain}
                              r={r}
                              selected={selected === r.domain}
                              onBuy={() => onBuy(r.domain)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </section>
        )}

        {/* ─── Your domains ─── */}
        <section>
          {managedLoading ? (
            <div className="py-20 text-center text-[13px] text-secondary">Loading domains…</div>
          ) : !hasDomains ? (
            // Canonical dashed-radial empty state (matches Projects.tsx).
            <div
              className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-subtle bg-surface px-9 py-12 text-center"
              style={{
                backgroundImage:
                  'radial-gradient(ellipse 70% 80% at 50% 100%, rgba(217,242,106,0.05) 0%, transparent 60%)',
              }}
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-accent/25 bg-accent/[0.08] text-accent">
                <div className="i-ph:globe-hemisphere-west text-base" />
              </div>
              <h3 className="mb-2 font-instrument text-[28px] leading-tight text-primary">No domains yet.</h3>
              <p className="mx-auto max-w-[380px] text-[13.5px] leading-[1.6] text-secondary">
                Search above to register your first domain. Dunea sets up DNS, provisions free SSL, and connects it to
                your project — automatically.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {/* Awaiting payment — unpaid orders the user can complete or remove */}
              {groups.pending.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <h2 className="ui-caption">Awaiting payment</h2>
                    <span className="inline-flex items-center gap-1.5 text-[10.5px] text-warning">
                      <div className="i-ph:clock text-[11px]" />
                      {groups.pending.length} not paid
                    </span>
                  </div>
                  <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
                    {groups.pending.map((d: ManagedDomain) => (
                      <DomainCard
                        key={d.domain}
                        d={d}
                        onOpen={() => navigate(`/domains/${encodeURIComponent(d.domain)}`)}
                        onResume={() => d.id && requirePaidPlan() && resumeMutation.mutate(d.id)}
                        onRemove={() => d.id && removeMutation.mutate(d.id)}
                        busy={resumeMutation.isPending || removeMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* In progress — purchases still being fulfilled (live) */}
              {groups.inProgress.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <h2 className="ui-caption">In progress</h2>
                    <span className="inline-flex items-center gap-1.5 text-[10.5px] text-info">
                      <span className="ui-dot bg-info animate-pulse" />
                      {groups.inProgress.length} setting up
                    </span>
                  </div>
                  <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
                    {groups.inProgress.map((d: ManagedDomain) => (
                      <DomainCard
                        key={d.domain}
                        d={d}
                        onOpen={() => navigate(`/domains/${encodeURIComponent(d.domain)}`)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Settled — active, connected, or failed */}
              {groups.settled.length > 0 && (
                <div>
                  <h2 className="ui-caption mb-3">Your domains</h2>
                  <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
                    {groups.settled.map((d: ManagedDomain) => (
                      <DomainCard
                        key={d.domain}
                        d={d}
                        onOpen={() => navigate(`/domains/${encodeURIComponent(d.domain)}`)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {/* ─── Purchase modal (deployment REQUIRED) — centered so there's no scrolling ─── */}
      {/* Hidden while the registrant form is open so the two modals never stack. */}
      {selected && !registrantModal && (
        <div className="ui-backdrop" onClick={() => setSelected(null)}>
          <div
            className="ui-modal max-h-[calc(100dvh-24px)] w-full max-w-md overflow-y-auto animate-[ui-slide-in-right_200ms_ease-out] sm:max-h-[92dvh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <div className="i-ph:shopping-cart-simple text-[16px] text-accent" />
              <h3 className="text-[14px] font-medium text-primary">Confirm your domain</h3>
              <button className="ui-btn-ghost ml-auto px-2" aria-label="Close" onClick={() => setSelected(null)}>
                <div className="i-ph:x text-[14px]" />
              </button>
            </div>

            <div className="ui-modal-body">
              {/* Domain hero */}
              <div className="mb-5 flex flex-col items-center rounded-xl border border-hairline bg-sunken/50 px-4 py-6 text-center">
                <div className="i-ph:globe mb-2.5 text-3xl text-accent" />
                <div className="ui-mono break-all text-[18px] font-medium text-primary">{selected}</div>
                <span className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-success">
                  <span className="ui-dot ui-dot-ok" />
                  Available · registered in your name
                </span>
              </div>

              {/* Price breakdown */}
              <div className={classNames(CARD, 'mb-4 divide-y divide-hairline px-4')}>
                <div className="flex items-center justify-between py-3">
                  <span className="text-[12.5px] text-secondary">Registration (1 year)</span>
                  <span className="text-[14px] font-semibold text-primary [font-variant-numeric:tabular-nums]">
                    {fmtMoney(selectedResult?.price?.registerPrice ?? null, selectedResult?.price?.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-[12.5px] text-muted">Renews at</span>
                  <span className="text-[12.5px] text-muted [font-variant-numeric:tabular-nums]">
                    {fmtMoney(selectedResult?.price?.renewPrice ?? null, selectedResult?.price?.currency)}/yr
                  </span>
                </div>
              </div>

              {/* Deployment selector */}
              {liveDeployments.length === 0 ? (
                <div className="ui-banner-warning mb-4">
                  <div className="i-ph:warning shrink-0 text-warning" />
                  You have no live deployments yet. Deploy a project first, then buy a domain to connect it.
                </div>
              ) : (
                <div className="mb-4">
                  <label className="ui-label">Connect to project</label>
                  <div className="relative">
                    <select
                      value={deploymentId}
                      onChange={(e) => setDeploymentId(e.target.value)}
                      aria-label="Deployment to connect"
                      className="ui-input appearance-none pr-9"
                    >
                      {liveDeployments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.subdomain}.dunea.app
                        </option>
                      ))}
                    </select>
                    <div className="i-ph:caret-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" />
                  </div>
                </div>
              )}

              {/* What's included, automatically */}
              <div className="mb-4 space-y-2 rounded-lg border border-hairline bg-sunken/40 p-3.5">
                {[
                  { icon: 'i-ph:lightning', label: 'Automatic DNS configuration' },
                  { icon: 'i-ph:lock-key', label: 'Automatic SSL certificate' },
                  { icon: 'i-ph:plugs-connected', label: 'Auto-connected to your project' },
                ].map((f) => (
                  <div key={f.label} className="flex items-center gap-2.5 text-[12.5px] text-secondary">
                    <div className="i-ph:check-bold shrink-0 text-[11px] text-success" />
                    <div className={classNames(f.icon, 'text-[14px] text-muted')} />
                    {f.label}
                  </div>
                ))}
              </div>

              {/* Registrant status */}
              <div className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted">
                <div
                  className={classNames(
                    'i-ph:user-circle text-[14px]',
                    registrant?.hasContact ? 'text-success' : 'text-muted',
                  )}
                />
                {registrant?.hasContact ? (
                  <>
                    Registered to{' '}
                    <span className="text-secondary">
                      {registrant.firstName} {registrant.lastName}
                    </span>
                    <button
                      className="text-accent hover:underline touch:min-h-10 touch:px-1"
                      onClick={() => setRegistrantModal('edit')}
                    >
                      Edit
                    </button>
                  </>
                ) : (
                  <>You&apos;ll add your registrant details next (one-time).</>
                )}
              </div>

              <button
                className="ui-btn-accent w-full justify-center"
                disabled={!deploymentId || purchaseMutation.isPending}
                onClick={startPurchase}
              >
                {purchaseMutation.isPending ? (
                  <>
                    <div className="i-svg-spinners:90-ring-with-bg text-[15px]" />
                    Starting checkout…
                  </>
                ) : (
                  <>
                    {registrant?.hasContact ? 'Continue to checkout' : 'Add details & continue'}
                    <div className="i-ph:arrow-right text-[14px]" />
                  </>
                )}
              </button>
              <button className="ui-btn-ghost mt-2 w-full justify-center" onClick={() => setSelected(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registrant Information modal — gate before first purchase, or edit later */}
      {registrantModal && (
        // Full-screen sheet on phones: a long form plus the keyboard doesn't fit a centered card.
        <div className="ui-backdrop lt-sm:!p-0" onClick={() => setRegistrantModal(null)}>
          <div
            className="ui-modal w-full max-w-2xl overflow-y-auto lt-sm:h-full lt-sm:rounded-none lt-sm:border-0 sm:max-h-[90dvh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <div className="i-ph:user-circle text-[16px] text-accent" />
              <h3 className="text-[14px] font-medium text-primary">Registrant information</h3>
              <button className="ui-btn-ghost ml-auto px-2" aria-label="Close" onClick={() => setRegistrantModal(null)}>
                <div className="i-ph:x text-[14px]" />
              </button>
            </div>
            <div className="ui-modal-body">
              <RegistrantForm
                initial={registrant}
                onCancel={() => setRegistrantModal(null)}
                onSaved={() => {
                  const proceed = registrantModal === 'gate';
                  setRegistrantModal(null);
                  refetchRegistrant();

                  // If this was the pre-purchase gate, continue straight to checkout.
                  if (proceed && selected && deploymentId) {
                    purchaseMutation.mutate({ domain: selected, dep: deploymentId });
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
